/**
 * Icon-driven filter strip for Browse (Iter 67l).
 *
 * Replaces the previous Search-bar style filter that duplicated the hero
 * stepper (Pickup / Drop-off / Dates). Those selections now live on the hero
 * + the ModifySearchPill above this bar. Here we only surface the *refinement*
 * filters renters tweak while scanning results: Category, Transmission, Fuel,
 * Seats, Price.
 *
 * Each option is a chip with a coloured lucide icon so the bar reads as a row
 * of visual affordances rather than a wall of selects.
 */
import { useState, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Car, Mountain, Crown, CarFront, Caravan, Bus, Truck,
  Cog, ChevronDown, Fuel, Zap, Leaf, Flame, Droplets,
  Users, BadgeEuro, RotateCcw, PawPrint, CigaretteOff,
  Sparkles,
} from "lucide-react";

// Categories with bespoke lucide glyphs. Falls back to `Car` for any future
// type we add without remembering to update this map.
// Iter 67ec — added Economy, Compact, Minivan, Wagon/Estate, Electric & Hybrid
// to match the expanded car-creation list.
const CATEGORIES = [
  { id: "Economy",            icon: Sparkles, color: "text-lime-600",    ring: "border-lime-200 bg-lime-50",       active: "bg-lime-600" },
  { id: "Compact",            icon: Car,      color: "text-cyan-600",    ring: "border-cyan-200 bg-cyan-50",       active: "bg-cyan-600" },
  { id: "Hatchback",          icon: Car,      color: "text-violet-600",  ring: "border-violet-200 bg-violet-50",   active: "bg-violet-600" },
  { id: "Sedan",              icon: CarFront, color: "text-indigo-600",  ring: "border-indigo-200 bg-indigo-50",   active: "bg-indigo-600" },
  { id: "Wagon / Estate",     icon: Caravan,  color: "text-blue-600",    ring: "border-blue-200 bg-blue-50",       active: "bg-blue-600" },
  { id: "Coupe",              icon: Car,      color: "text-rose-600",    ring: "border-rose-200 bg-rose-50",       active: "bg-rose-600" },
  { id: "Convertible",        icon: Car,      color: "text-fuchsia-600", ring: "border-fuchsia-200 bg-fuchsia-50", active: "bg-fuchsia-600" },
  { id: "SUV",                icon: Car,      color: "text-sky-600",     ring: "border-sky-200 bg-sky-50",         active: "bg-sky-600" },
  { id: "SUV 4x4",            icon: Mountain, color: "text-emerald-600", ring: "border-emerald-200 bg-emerald-50", active: "bg-emerald-600" },
  { id: "Minivan",            icon: Bus,      color: "text-purple-600",  ring: "border-purple-200 bg-purple-50",   active: "bg-purple-600" },
  { id: "Van",                icon: Bus,      color: "text-teal-600",    ring: "border-teal-200 bg-teal-50",       active: "bg-teal-600" },
  { id: "Truck",              icon: Truck,    color: "text-orange-600",  ring: "border-orange-200 bg-orange-50",   active: "bg-orange-600" },
  { id: "Luxury",             icon: Crown,    color: "text-amber-600",   ring: "border-amber-200 bg-amber-50",     active: "bg-amber-600" },
  { id: "Electric & Hybrid",  icon: Zap,      color: "text-emerald-600", ring: "border-emerald-200 bg-emerald-50", active: "bg-emerald-600" },
];

const TRANSMISSIONS = [
  { id: "Automatic", icon: Cog },
  { id: "Manual",    icon: Cog },
];

const FUELS = [
  { id: "Gasoline", icon: Fuel,     color: "text-amber-600" },
  { id: "Diesel",   icon: Droplets, color: "text-stone-600" },
  { id: "Electric", icon: Zap,      color: "text-emerald-600" },
  { id: "Hybrid",   icon: Leaf,     color: "text-lime-600" },
  { id: "LPG",      icon: Flame,    color: "text-orange-600" },
];

function FilterPopover({ trigger, children, testId }) {
  return (
    <Popover>
      <PopoverTrigger asChild data-testid={testId}>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-4 rounded-2xl">
        {children}
      </PopoverContent>
    </Popover>
  );
}

// Iter 74o — Radix's Popover uses `asChild` to attach onClick + refs to the
// immediate child. React function components are not ref-forwarding by
// default, so the props Radix injected never reached the underlying <button>
// and clicks did nothing. Wrapping this component in `forwardRef` and
// spreading `...rest` onto the <button> fixes the "filters don't open" bug.
const TriggerChip = forwardRef(function TriggerChip(
  { icon: Icon, color, label, value, active, testId, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition shrink-0 ${
        active
          ? "bg-indigo-600 text-white border-indigo-700 shadow-sm"
          : "bg-white border-border text-neutral-700 hover:border-indigo-300 hover:bg-indigo-50"
      }`}
      data-testid={testId}
      {...rest}
    >
      <Icon className={`h-3.5 w-3.5 ${active ? "text-white" : color}`} />
      <span>{label}</span>
      {value && <span className="opacity-90">· {value}</span>}
      <ChevronDown className="h-3 w-3 opacity-60" />
    </button>
  );
});

export default function BrowseFilterStrip({
  carType, setCarType,
  trans, setTrans,
  fuel, setFuel,
  seats, setSeats,
  price, setPrice,
  petFriendly, setPetFriendly,
  smokingAllowed, setSmokingAllowed,
  resultCount = 0,
  onApply, onReset,
  // Iter 69 — "strip" (default) is the sticky horizontal scroll row at the top
  // of Browse. "sheet" is the expanded vertical layout used inside the mobile
  // Filter sheet, where chips would otherwise overflow off-screen and their
  // popovers don't fit comfortably.
  variant = "strip",
}) {
  const { t } = useTranslation();
  // Track the "draft" version of price so the slider feels responsive without
  // firing a backend call on every tick. Apply on "Done" or popover close.
  const [draftPrice, setDraftPrice] = useState(price);

  const activeCount =
    (carType ? 1 : 0) +
    (trans ? 1 : 0) +
    (fuel ? 1 : 0) +
    (seats > 0 ? 1 : 0) +
    ((price[0] > 0 || price[1] < 500) ? 1 : 0) +
    (petFriendly ? 1 : 0) +
    (smokingAllowed ? 1 : 0);

  const applyChange = (next) => {
    // Optimistic: parent state has already updated, kick off a fresh fetch.
    setTimeout(() => onApply(), 0);
  };

  // ---- Per-filter BODIES — shared between the strip's popovers and the
  // sheet's stacked layout. Each returns the option grid only; the section
  // label is added separately so the sheet can render its own headers.
  const renderCategoryBody = () => (
    <div className="grid grid-cols-3 gap-2">
      {CATEGORIES.map((c) => {
        const isOn = carType === c.id;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => { setCarType(isOn ? "" : c.id); applyChange(); }}
            className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-2xl border transition ${
              isOn ? `${c.active} text-white border-transparent shadow-sm` : `${c.ring} hover:brightness-95`
            }`}
            data-testid={`filter-type-${c.id}`}
          >
            <c.icon className={`h-5 w-5 ${isOn ? "text-white" : c.color}`} />
            <span className={`text-[11px] font-semibold ${isOn ? "text-white" : "text-neutral-800"}`}>{c.id}</span>
          </button>
        );
      })}
    </div>
  );

  const renderTransBody = () => (
    <div className="grid grid-cols-2 gap-2">
      {TRANSMISSIONS.map((tr) => {
        const isOn = trans === tr.id;
        return (
          <button
            key={tr.id}
            type="button"
            onClick={() => { setTrans(isOn ? "" : tr.id); applyChange(); }}
            className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-2xl border transition ${
              isOn ? "bg-indigo-600 text-white border-transparent shadow-sm" : "border-indigo-200 bg-indigo-50 hover:brightness-95"
            }`}
            data-testid={`filter-trans-${tr.id}`}
          >
            <tr.icon className={`h-5 w-5 ${isOn ? "text-white" : "text-indigo-600"}`} />
            <span className={`text-[11px] font-semibold ${isOn ? "text-white" : "text-neutral-800"}`}>{tr.id}</span>
          </button>
        );
      })}
    </div>
  );

  const renderFuelBody = () => (
    <div className="grid grid-cols-3 gap-2">
      {FUELS.map((f) => {
        const isOn = fuel === f.id;
        return (
          <button
            key={f.id}
            type="button"
            onClick={() => { setFuel(isOn ? "" : f.id); applyChange(); }}
            className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-2xl border transition ${
              isOn ? "bg-neutral-900 text-white border-transparent shadow-sm" : "border-border bg-white hover:bg-neutral-50"
            }`}
            data-testid={`filter-fuel-${f.id}`}
          >
            <f.icon className={`h-5 w-5 ${isOn ? "text-white" : f.color}`} />
            <span className={`text-[11px] font-semibold ${isOn ? "text-white" : "text-neutral-800"}`}>{f.id}</span>
          </button>
        );
      })}
    </div>
  );

  const renderSeatsBody = () => (
    <>
      <div className="grid grid-cols-4 gap-2">
        {[2, 4, 5, 7, 8, 9].map((n) => {
          const isOn = seats === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => { setSeats(isOn ? 0 : n); applyChange(); }}
              className={`px-2 py-2 rounded-xl border text-sm font-semibold transition ${
                isOn ? "bg-emerald-600 text-white border-transparent" : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:brightness-95"
              }`}
              data-testid={`filter-seats-${n}`}
            >
              {n}+
            </button>
          );
        })}
      </div>
      <Input
        type="number"
        min="0"
        max="10"
        className="rounded-xl h-9 mt-3"
        placeholder={t("filter.customAny")}
        value={seats || ""}
        onChange={(e) => setSeats(Number(e.target.value) || 0)}
        onBlur={() => applyChange()}
        data-testid="filter-seats-custom"
      />
    </>
  );

  const renderPriceBody = () => (
    <>
      <Label className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-3 block">
        {t("filter.pricePerDay")} · €{draftPrice[0]} – €{draftPrice[1]}{draftPrice[1] >= 500 && "+"}
      </Label>
      <Slider
        value={draftPrice}
        onValueChange={setDraftPrice}
        onValueCommit={(v) => { setPrice(v); applyChange(); }}
        min={0}
        max={500}
        step={10}
        data-testid="filter-price"
      />
      <div className="flex justify-between text-[11px] text-neutral-500 mt-2">
        <span>€0</span><span>€500+</span>
      </div>
    </>
  );

  // ---- Sheet variant — stack every filter section vertically so they are
  // all visible and tappable inside the mobile bottom sheet.
  if (variant === "sheet") {
    return (
      <div className="space-y-5" data-testid="browse-filter-sheet-body">
        <Section title={t("filter.vehicleCategory")}>{renderCategoryBody()}</Section>
        <Section title={t("filter.transmissionLabel")}>{renderTransBody()}</Section>
        <Section title={t("filter.fuelTypeLabel")}>{renderFuelBody()}</Section>
        <Section title={t("filter.minimumSeats")}>{renderSeatsBody()}</Section>
        <Section title={t("filter.priceDay")}>{renderPriceBody()}</Section>

        {/* Boolean toggles as full-width rows for tap comfort */}
        <Section title={t("filter.preferences", { defaultValue: "Preferences" })}>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => { setPetFriendly(!petFriendly); applyChange(); }}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm font-semibold transition ${
                petFriendly
                  ? "bg-emerald-600 text-white border-emerald-700 shadow-sm"
                  : "bg-white border-border text-neutral-700 hover:border-emerald-300 hover:bg-emerald-50"
              }`}
              aria-pressed={petFriendly}
              data-testid="filter-pet-friendly-chip"
            >
              <PawPrint className={`h-4 w-4 ${petFriendly ? "text-white" : "text-emerald-600"}`} />
              <span>{t("filter.petFriendly")}</span>
            </button>
            <button
              type="button"
              onClick={() => { setSmokingAllowed(!smokingAllowed); applyChange(); }}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm font-semibold transition ${
                smokingAllowed
                  ? "bg-rose-600 text-white border-rose-700 shadow-sm"
                  : "bg-white border-border text-neutral-700 hover:border-rose-300 hover:bg-rose-50"
              }`}
              aria-pressed={smokingAllowed}
              data-testid="filter-smoking-allowed-chip"
            >
              <CigaretteOff className={`h-4 w-4 ${smokingAllowed ? "text-white" : "text-rose-600"}`} />
              <span>{t("filter.smokingOk")}</span>
            </button>
          </div>
        </Section>

        {/* Sticky footer-row inside the scrollable area: Reset + active count */}
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="text-[11px] font-semibold text-neutral-500">
            {resultCount} {t("browse.match", { count: resultCount })}
            {activeCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1 rounded-full bg-indigo-600 text-white text-[10px] font-bold">
                {activeCount}
              </span>
            )}
          </div>
          {activeCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onReset}
              className="rounded-full h-8 text-[11px] gap-1 text-neutral-600 hover:text-neutral-900"
              data-testid="clear-all-filters"
            >
              <RotateCcw className="h-3 w-3" /> {t("filter.clear")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-border/60 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 mb-6" data-testid="browse-filter-strip">
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {/* ── Category ─────────────────────────────────── */}
        <FilterPopover
          testId="filter-category-trigger"
          trigger={
            <TriggerChip
              icon={Car}
              color="text-sky-600"
              label={t("filter.category")}
              value={carType}
              active={!!carType}
              testId="filter-category-chip"
            />
          }
        >
          <Label className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-3 block">{t("filter.vehicleCategory")}</Label>
          {renderCategoryBody()}
        </FilterPopover>

        {/* ── Transmission ──────────────────────────────── */}
        <FilterPopover
          testId="filter-trans-trigger"
          trigger={
            <TriggerChip
              icon={Cog}
              color="text-indigo-600"
              label={t("filter.gearbox")}
              value={trans}
              active={!!trans}
              testId="filter-trans-chip"
            />
          }
        >
          <Label className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-3 block">{t("filter.transmissionLabel")}</Label>
          {renderTransBody()}
        </FilterPopover>

        {/* ── Fuel ──────────────────────────────────────── */}
        <FilterPopover
          testId="filter-fuel-trigger"
          trigger={
            <TriggerChip
              icon={Fuel}
              color="text-amber-600"
              label={t("filter.fuel")}
              value={fuel}
              active={!!fuel}
              testId="filter-fuel-chip"
            />
          }
        >
          <Label className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-3 block">{t("filter.fuelTypeLabel")}</Label>
          {renderFuelBody()}
        </FilterPopover>

        {/* ── Seats ─────────────────────────────────────── */}
        <FilterPopover
          testId="filter-seats-trigger"
          trigger={
            <TriggerChip
              icon={Users}
              color="text-emerald-600"
              label={t("filter.seats")}
              value={seats > 0 ? `${seats}+` : ""}
              active={seats > 0}
              testId="filter-seats-chip"
            />
          }
        >
          <Label className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-3 block">{t("filter.minimumSeats")}</Label>
          {renderSeatsBody()}
        </FilterPopover>

        {/* ── Price ─────────────────────────────────────── */}
        <FilterPopover
          testId="filter-price-trigger"
          trigger={
            <TriggerChip
              icon={BadgeEuro}
              color="text-rose-600"
              label={t("filter.priceDay")}
              value={(price[0] > 0 || price[1] < 500) ? `€${price[0]}–€${price[1]}${price[1] >= 500 ? "+" : ""}` : ""}
              active={(price[0] > 0 || price[1] < 500)}
              testId="filter-price-chip"
            />
          }
        >
          {renderPriceBody()}
        </FilterPopover>

        {/* ── Pet-friendly (boolean toggle) ─────────────── */}
        <button
          type="button"
          onClick={() => { setPetFriendly(!petFriendly); applyChange(); }}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition shrink-0 ${
            petFriendly
              ? "bg-emerald-600 text-white border-emerald-700 shadow-sm"
              : "bg-white border-border text-neutral-700 hover:border-emerald-300 hover:bg-emerald-50"
          }`}
          aria-pressed={petFriendly}
          data-testid="filter-pet-friendly-chip"
        >
          <PawPrint className={`h-3.5 w-3.5 ${petFriendly ? "text-white" : "text-emerald-600"}`} />
          <span>{t("filter.petFriendly")}</span>
        </button>

        {/* ── Smoking allowed (boolean toggle) ──────────── */}
        <button
          type="button"
          onClick={() => { setSmokingAllowed(!smokingAllowed); applyChange(); }}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition shrink-0 ${
            smokingAllowed
              ? "bg-rose-600 text-white border-rose-700 shadow-sm"
              : "bg-white border-border text-neutral-700 hover:border-rose-300 hover:bg-rose-50"
          }`}
          aria-pressed={smokingAllowed}
          data-testid="filter-smoking-allowed-chip"
        >
          <CigaretteOff className={`h-3.5 w-3.5 ${smokingAllowed ? "text-white" : "text-rose-600"}`} />
          <span>{t("filter.smokingOk")}</span>
        </button>

        {activeCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="ml-1 rounded-full h-8 text-[11px] gap-1 text-neutral-600 hover:text-neutral-900"
            data-testid="clear-all-filters"
          >
            <RotateCcw className="h-3 w-3" /> {t("filter.clear")}
          </Button>
        )}

        <div className="ml-auto pl-3 shrink-0 text-[11px] font-semibold text-neutral-500 hidden sm:block">
          {resultCount} {t("browse.match", { count: resultCount })}
          {activeCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1 rounded-full bg-indigo-600 text-white text-[10px] font-bold">
              {activeCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-2 block">{title}</Label>
      {children}
    </div>
  );
}
